import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  Chip,
  Fab,
  IconButton,
  InputBase,
  Link,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import ChatIcon from '@material-ui/icons/Chat';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {discoveryApiRef, fetchApiRef, useApi} from '@backstage/core-plugin-api';
import {useLocation} from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type SourceItem = {
  type: string;
  label: string;
  url?: string;
};

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
  answer?: string;
  sources?: SourceItem[];
  dataSources?: string[];
  showSources?: boolean;
};

/** Extract entityRef from URL like /catalog/default/component/my-service/... */
function extractEntityRef(pathname: string): string {
  const m = pathname.match(/^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/);
  if (!m) return '';
  const [, namespace, kind, name] = m;
  return `${kind}:${namespace}/${name}`.toLowerCase();
}

function extractEntityName(pathname: string): string {
  const m = pathname.match(/^\/catalog\/[^/]+\/[^/]+\/([^/]+)/);
  return m ? m[1] : '';
}

const SOURCE_COLORS: Record<string, string> = {
  confluence: '#0469E3',
  jira: '#0052CC',
  aws: '#FF9900',
  jenkins: '#D33833',
  grafana: '#3EC28F',
  prometheus: '#E6522C',
};

const SOURCE_LABELS: Record<string, string> = {
  confluence: 'Confluence',
  jira: 'Jira',
  aws: 'AWS',
  jenkins: 'Jenkins',
  grafana: 'Grafana',
  prometheus: 'Prometheus',
};

const useStyles = makeStyles(() => ({
  fab: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 1300,
    backgroundColor: '#0469E3',
    color: '#fff',
    '&:hover': {backgroundColor: '#0356b8'},
  },
  chatContainer: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 480,
    height: 600,
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #0469E3 0%, #060C3A 100%)',
    color: '#fff',
  },
  chatBody: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: 12,
    backgroundColor: '#f5f5f5',
  },
  chatInputRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  inputBase: {
    flex: 1,
    fontSize: 14,
    padding: '6px 8px',
  },
  userMsg: {
    alignSelf: 'flex-end',
    backgroundColor: '#0469E3',
    color: '#fff',
    padding: '8px 14px',
    borderRadius: '16px 16px 4px 16px',
    maxWidth: '80%',
    marginBottom: 8,
    fontSize: 13,
    wordBreak: 'break-word' as const,
  },
  assistantMsg: {
    alignSelf: 'flex-start',
    backgroundColor: '#fff',
    color: '#333',
    padding: '10px 14px',
    borderRadius: '16px 16px 16px 4px',
    maxWidth: '90%',
    marginBottom: 8,
    fontSize: 13,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  sourceCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '5px 10px',
    marginTop: 3,
    borderRadius: 6,
    backgroundColor: '#f0f4ff',
    border: '1px solid #dce4f0',
  },
}));

export function ConfluenceChatbox() {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const location = useLocation();

  const entityRef = useMemo(() => extractEntityRef(location.pathname), [location.pathname]);
  const entityName = useMemo(() => extractEntityName(location.pathname), [location.pathname]);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableSources, setAvailableSources] = useState<Record<string, boolean>>({});
  const [sourcesLoaded, setSourcesLoaded] = useState(false);

  // Inject keyframes for chat loading animation
  useEffect(() => {
    const id = 'chat-pulse-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes chatPulse {
        0%, 100% { transform: scale(1); opacity: 0.5; }
        50% { transform: scale(1.6); opacity: 1; }
      }
      @keyframes chatFadeSlide {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
      @keyframes chatDotBounce {
        0%, 100% { transform: translateY(0); opacity: 0.3; }
        50% { transform: translateY(-3px); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Reset when entity changes
  useEffect(() => {
    setMessages([]);
    setAvailableSources({});
    setSourcesLoaded(false);
  }, [entityRef]);

  // Load available sources
  const loadSources = useCallback(async () => {
    if (!entityRef) {
      setSourcesLoaded(true);
      return;
    }
    try {
      const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
      const resp = await fetchApi.fetch(`${baseUrl}/sources?entityRef=${encodeURIComponent(entityRef)}`);
      const data = await resp.json();
      setAvailableSources(data);
    } catch {
      // ignore
    } finally {
      setSourcesLoaded(true);
    }
  }, [discoveryApi, fetchApi, entityRef]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const hasAnySources = Object.values(availableSources).some(Boolean);

  const handleSend = async () => {
    const q = input.trim();
    if (!q) return;

    const updatedMessages = [...messages, {role: 'user' as const, content: q}];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    // Build last 5 conversation pairs (10 messages) for context
    const history = updatedMessages.slice(-10).map(m => ({
      role: m.role,
      content: m.role === 'assistant' ? (m.answer || m.content) : m.content,
    }));

    try {
      const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
      const resp = await fetchApi.fetch(`${baseUrl}/ask`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({query: q, entityRef, history}),
      });
      const data = await resp.json();

      if (data.error && !data.answer) {
        setMessages(prev => [...prev, {role: 'assistant', content: data.error}]);
      } else {
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: '',
            answer: data.answer,
            sources: data.sources || [],
            dataSources: data.dataSources || [],
            showSources: false,
          },
        ]);
      }
    } catch (err: any) {
      setMessages(prev => [...prev, {role: 'assistant', content: `Error: ${err.message}`}]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'ArrowUp' && !input) {
      e.preventDefault();
      const userMsgs = messages.filter(m => m.role === 'user');
      if (userMsgs.length > 0) {
        setInput(userMsgs[userMsgs.length - 1].content);
      }
    }
  };

  const chatBodyRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Only show on entity pages
  if (!entityRef) return null;

  // Don't show until we know sources, and hide if none configured
  if (!sourcesLoaded) return null;
  if (!hasAnySources) return null;

  if (!open) {
    return (
      <Tooltip title="AI Assistant">
        <Fab className={classes.fab} onClick={() => setOpen(true)}>
          <ChatIcon />
        </Fab>
      </Tooltip>
    );
  }

  const configuredSources = Object.entries(availableSources)
    .filter(([, v]) => v)
    .map(([k]) => k);

  return (
    <>
      <Paper className={classes.chatContainer} elevation={8}>
        {/* Header */}
        <Box className={classes.chatHeader}>
          <Box display="flex" alignItems="center" style={{flexWrap: 'wrap', gap: 4}}>
            <Typography variant="subtitle1" style={{fontWeight: 600, fontSize: 15, marginRight: 6}}>
              AI Assistant
            </Typography>
            {entityName && (
              <Chip
                label={entityName}
                size="small"
                style={{backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 10, height: 18}}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center">
            {configuredSources.map(s => (
              <Tooltip key={s} title={`${s} connected`}>
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: SOURCE_COLORS[s] || '#999',
                    marginRight: 4,
                  }}
                />
              </Tooltip>
            ))}
            <IconButton size="small" style={{color: '#fff', marginLeft: 4}} onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Body */}
        <Box className={classes.chatBody} ref={chatBodyRef} style={{display: 'flex', flexDirection: 'column'}}>
          {messages.length === 0 ? (
            <Box textAlign="center" mt={4} px={2}>
              <Typography variant="body2" style={{color: '#888', marginBottom: 12}}>
                Ask about docs, tickets, builds, infrastructure...
              </Typography>
              <Box display="flex" justifyContent="center" flexWrap="wrap" style={{gap: 4}}>
                {configuredSources.map(s => (
                  <Chip
                    key={s}
                    label={s}
                    size="small"
                    style={{
                      backgroundColor: SOURCE_COLORS[s] || '#999',
                      color: '#fff',
                      fontSize: 10,
                      height: 20,
                      textTransform: 'capitalize',
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            messages.map((msg, i) => (
              <Box key={i} className={msg.role === 'user' ? classes.userMsg : classes.assistantMsg}>
                {msg.content && (
                  <Typography variant="body2" style={{fontSize: 13}}>{msg.content}</Typography>
                )}

                {/* Data source badges */}
                {msg.dataSources && msg.dataSources.length > 0 && (
                  <Box display="flex" flexWrap="wrap" style={{gap: 4, marginBottom: 6}}>
                    {msg.dataSources.map(ds => (
                      <Chip
                        key={ds}
                        label={ds}
                        size="small"
                        style={{
                          backgroundColor: SOURCE_COLORS[ds] || '#999',
                          color: '#fff',
                          fontSize: 10,
                          height: 18,
                          textTransform: 'capitalize',
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Answer */}
                {msg.answer && (
                  <Box style={{fontSize: 13, lineHeight: 1.6}}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({children}) => <p style={{margin: '4px 0'}}>{children}</p>,
                        strong: ({children}) => <strong style={{fontWeight: 600}}>{children}</strong>,
                        ul: ({children}) => <ul style={{margin: '4px 0', paddingLeft: 18}}>{children}</ul>,
                        ol: ({children}) => <ol style={{margin: '4px 0', paddingLeft: 18}}>{children}</ol>,
                        li: ({children}) => <li style={{marginBottom: 2}}>{children}</li>,
                        code: ({children}) => (
                          <code style={{backgroundColor: '#f0f0f0', padding: '1px 4px', borderRadius: 3, fontSize: 12}}>
                            {children}
                          </code>
                        ),
                        table: ({children}) => (
                          <Box style={{overflowX: 'auto', margin: '8px 0'}}>
                            <table style={{borderCollapse: 'collapse', width: '100%', fontSize: 11}}>{children}</table>
                          </Box>
                        ),
                        thead: ({children}) => (
                          <thead style={{backgroundColor: '#f0f4ff'}}>{children}</thead>
                        ),
                        th: ({children}) => (
                          <th style={{
                            padding: '6px 8px', textAlign: 'left', fontWeight: 600, fontSize: 10,
                            borderBottom: '2px solid #dce4f0', whiteSpace: 'nowrap', color: '#555',
                          }}>{children}</th>
                        ),
                        td: ({children}) => (
                          <td style={{
                            padding: '5px 8px', borderBottom: '1px solid #eee', fontSize: 11, color: '#333',
                          }}>{children}</td>
                        ),
                        tr: ({children}) => <tr>{children}</tr>,
                      }}
                    >
                      {msg.answer}
                    </ReactMarkdown>
                  </Box>
                )}

                {/* Sources */}
                {msg.sources && msg.sources.length > 0 && (
                  <Box mt={1}>
                    <Button
                      size="small"
                      style={{textTransform: 'none', fontSize: 11, color: '#888', padding: '2px 6px'}}
                      onClick={() => {
                        setMessages(prev => prev.map((m, mi) =>
                          mi === i ? {...m, showSources: !m.showSources} : m,
                        ));
                      }}
                    >
                      {msg.showSources ? 'Hide' : 'Show'} {msg.sources.length} source(s)
                    </Button>
                    {msg.showSources && msg.sources.map((s, si) => (
                      <Box key={si} className={classes.sourceCard}>
                        <Box display="flex" alignItems="center" style={{gap: 6, flex: 1, minWidth: 0}}>
                          <Box
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: SOURCE_COLORS[s.type] || '#999',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            style={{fontWeight: 500, color: '#333', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                          >
                            {s.label}
                          </Typography>
                        </Box>
                        {s.url && (
                          <Link href={s.url} target="_blank" rel="noopener noreferrer">
                            <OpenInNewIcon style={{fontSize: 14, color: '#0469E3'}} />
                          </Link>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          )}

          {loading && (
            <Box
              display="flex"
              alignItems="center"
              style={{
                alignSelf: 'flex-start',
                backgroundColor: '#fff',
                padding: '10px 16px',
                borderRadius: '16px 16px 16px 4px',
                marginBottom: 8,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                gap: 8,
              }}
            >
              <Box display="flex" alignItems="center" style={{gap: 5}}>
                {configuredSources.map((s, idx) => (
                  <Box
                    key={s}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: SOURCE_COLORS[s] || '#999',
                      animation: `chatPulse 1.4s ease-in-out ${idx * 0.2}s infinite`,
                      boxShadow: `0 0 6px ${SOURCE_COLORS[s] || '#999'}80`,
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" style={{color: '#999', fontSize: 11}}>
                Searching...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Input */}
        <Box className={classes.chatInputRow}>
          <InputBase
            className={classes.inputBase}
            placeholder="Ask about docs, tickets, builds, infrastructure..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <IconButton
            size="small"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={{color: '#0469E3'}}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </>
  );
}
